import { LitElement, html, css, PropertyValues } from 'lit';
import {property, customElement} from 'lit/decorators.js';
import { VesselPlanningModel, VoyagePlan } from '../../../src/models/vessel-planning-model';
import '../../voyage-popup/src/voyage-popup';
import { VoyTimeinput } from '../../voy-timeinput/src/voy-timeinput';
import '../../voy-timeinput/src/voy-timeinput';
import { VoyageSchedulesModel } from '../../../src/models/voyage-schedules-model';
import { VoyageDialogPopupManager } from '../../voyage-dialog-popup/src/voyage-dialog-popup-manager';
import { deepCopy, formatTooltipTime, getDeviationClass, tooltipText } from '../../../src/utils/helpers';
import { addPrefillValuesToPassagePlan, areArrivalEtasValid, areDepartureEtdsValid, ARRIVAL_ETA_VALIDATION_ERROR, DEPARTURE_ETD_VALIDATION_ERROR, recalcRoutes } from '../../../src/utils/route-utils';
import { verifyETAOverlapWithDepartureTime } from '../../../src/utils/helpers';
import { ScheduleEntry } from '../../../src/models/types/voyage-schedules-model.types';
import { VoyageImoModel } from '../../../src/models/voyage-imo-model';
import { SvgIcons } from '../../voyage-svg-icons/src/voyage-svg-icons';
import { until } from 'lit/directives/until';

const isVesselOnCloud = (<any>window)["appenv"].vesselOnCloud;

@customElement('voy-eta-etd-popup')
export class VoyEtaEtdPopup extends LitElement
{
    @property({type: Boolean}) shown: boolean = false;
    @property({type: Boolean}) etd: boolean = false;
    @property({type: String}) scheduleId: string = "";

    static get styles()
    {
        return [
            css`
            :host {
            }

            .container {
                display: flex;
                margin: 15px;
            }

            voy-timeinput:first-child {
                margin-right: 20px;
            }

            .label {
                border-radius: 100px;
                width: fit-content;
                padding: 5px 8px;
                display: flex;
                align-items: center;
                gap: 4px;
                margin-top: 4px;
                font-family: 'MaerskText'
            }

            .deviation {
                background-color: #FCDDE0;
                font-weight: bold;
            }

            .aligned {
                background-color: #DCF6D9;
                font-weight: bold;
            }

            .default{
                background: #EDEDED;
            }

            .lowdeviation{
                background-color: #FFF1C2;
                font-weight: bold;
            }

            .time-schedules {
                font-size: 12px;
            }

            .validation-text {
                color: var(--feedback-danger, red);
                display: block;
                font-size: 12px;
                margin-top: 4px;
                max-width: 260px;
            }

            .label svg{
                width: 16px;
                height: 16px;
                display: flex;
            }

            `
        ];
    }

    constructor()
    {
        super();

        this._readScheduleAndPlan = this._readScheduleAndPlan.bind(this);
        this.getLabelClass = this.getLabelClass.bind(this);
    }

    render()
    {
        const terminalId = !this._scheduleEntry ? "" : this.etd ? this._scheduleEntry.departureTerminalCode : this._scheduleEntry.arrivalTerminalCode;
        const editDepartureEtd = Boolean(this.etd);
        let headerText = this.etd ? "Edit Departure and Pilot Off time" : "Edit Pilot Station and Arrival time";
        const scheduleTimePrimary = editDepartureEtd ? this.scheduledDeparture : this.scheduledPilotArrival;
        const scheduleTimeSecondary = editDepartureEtd ? this.scheduledPilotDeparture : this.scheduledArrival;
        const scheduleTerminalPrimary = editDepartureEtd ? this._scheduleEntry?.departureTerminalCode : this._scheduleEntry?.arrivalTerminalCode;
        const scheduleTerminalSecondary = editDepartureEtd ? this._scheduleEntry?.departureTerminalCode : this._scheduleEntry?.arrivalTerminalCode;
        const pilotBerthTimesInvalid = this.etd ? !this._departureEtdsValid() : !this._arrivalEtasValid();
        return html`
        <voyage-popup accept-button-text="Save" .shown=${this.shown}
                      @accept=${this._applyChange}
                      @close=${() => this.dispatchEvent(new Event('close'))}
                      .headerText=${headerText}
                      .disableAcceptButton=${(
                            this.etd
                            ? this._input1Value === -1 || pilotBerthTimesInvalid
                            : this._input2Value === -1 || pilotBerthTimesInvalid
                          )}
                      .loading=${this._working}>
            <div class="container">
                <div>
                    <voy-timeinput label=${this.etd ? "ETD Berth " : "ETA Pilot "} .value=${this._input1Value} warn-utc
                                   .terminalId=${terminalId}
                                   .allowEmpty=${!this.etd}
                                   .readonly=${this.etd ? this._departureBerthSourceIsActual : this._pilotArrivalSourceIsActual}
                                   .invalid=${pilotBerthTimesInvalid}
                                   @input=${(evt:InputEvent) => this._input1Value = (<VoyTimeinput>evt.target).value }>
                    </voy-timeinput>
                    <div data-test="depts" class="time-schedules label ${this.getLabelClass(editDepartureEtd, this._input1Value, this.scheduledDeparture, this.scheduledPilotArrival)}"><span>${SvgIcons.lightExclamationCircle}
                    <voyage-tooltip-proxy
                        text=${tooltipText.default}
                        html-content
                        .positionPriority=${["bottom"]}>
                    </voyage-tooltip-proxy>
                    </span>${scheduleTimePrimary ? until(formatTooltipTime(scheduleTimePrimary, scheduleTerminalPrimary)) : "-"}${scheduleTimePrimary ? (false ? html` <span class="utcWarn">UTC</span>` : " LT") : ""} 
                    </div>
                    ${!this.etd && pilotBerthTimesInvalid ? html`<span class="validation-text">${ARRIVAL_ETA_VALIDATION_ERROR}</span>` : ""}
                </div>
                <div>
                    <voy-timeinput label=${this.etd ? "ETD Pilot " : "ETA Berth "} .value=${this._input2Value} warn-utc
                                   .terminalId=${terminalId}
                                   .allowEmpty=${this.etd}
                                   .readonly=${this.etd ? this._departureBerthSourceIsActual && this._pilotDepartureSourceIsActual : this._arrivalBerthIsActual}
                                   .invalid=${pilotBerthTimesInvalid}
                                   @input=${(evt:InputEvent) => this._input2Value = (<VoyTimeinput>evt.target).value }>
                    </voy-timeinput>
                    <div data-test="depts" class="time-schedules label ${this.getLabelClass(editDepartureEtd, this._input2Value, this.scheduledPilotDeparture, this.scheduledArrival)}"><span>${SvgIcons.lightExclamationCircle}
                    <voyage-tooltip-proxy
                        text=${tooltipText.default}
                        html-content
                        .positionPriority=${["bottom"]}>
                    </voyage-tooltip-proxy>
                    </span>${scheduleTimeSecondary ? until(formatTooltipTime(scheduleTimeSecondary, scheduleTerminalSecondary)) : "-"}${scheduleTimeSecondary ? (false ? html` <span class="utcWarn">UTC</span>` : " LT") : ""} 
                    </div>
                    ${this.etd && pilotBerthTimesInvalid ? html`<span class="validation-text">${DEPARTURE_ETD_VALIDATION_ERROR}</span>` : ""}
                </div>
            </div>
        </voyage-popup>
        `;
    }

    firstUpdated() {
        this._readScheduleAndPlan();
    }

    updated(changedProperties: PropertyValues)
    {
        if (changedProperties.has("shown") || changedProperties.has("etd") || changedProperties.has("scheduleId"))
        {
            this._readScheduleAndPlan();
        }
    }

    @property({ attribute: false }) private _scheduleEntry: ScheduleEntry|null = null;
    @property({ attribute: false }) private _input1Value: number = -1;
    @property({ attribute: false }) private _input2Value: number = -1;
    @property({ attribute: false }) private _working: boolean = false;
    @property({ attribute: false }) private _plan: VoyagePlan|null = null;

    private _arrivalEtasValid(): boolean {
        return this._input1Value === -1 || areArrivalEtasValid(this._plan, this._input1Value, this._input2Value);
    }

    private _departureEtdsValid(): boolean {
        return this._input2Value === -1 || areDepartureEtdsValid(this._input1Value, this._input2Value);
    }
    @property({ attribute: false }) private _departureBerthSourceIsActual: boolean = false;
    @property({attribute: false}) private _pilotDepartureSourceIsActual: boolean = false;
    @property({attribute: false}) private _pilotArrivalSourceIsActual: boolean = false;
    @property({attribute: false}) private _arrivalBerthIsActual: boolean = false;
    @property({attribute: false}) private scheduledDeparture: string|null = null;
    @property({attribute: false}) private scheduledArrival: string|null = null;
    @property({attribute: false}) private scheduledPilotDeparture: string|null = null;
    @property({attribute: false}) private scheduledPilotArrival: string|null = null;


    private async _readScheduleAndPlan(): Promise<void>
    {
        
        let scheduleEntry = null;
        this._input1Value = -1; //resetting value
        this._input2Value = -1;
        if (this.scheduleId)
        {
            const schedule = await VoyageSchedulesModel!.getScheduleFor(VoyageImoModel!.imo!) || [];
            for (const entry of schedule)
            {
                if (entry.id === this.scheduleId)
                {
                    scheduleEntry = entry;
                    break;
                }
            }

            const plan = await this._getPlan(scheduleEntry!);
            this._plan = plan;

            if (scheduleEntry)
            {
                if (this.etd)
                {
                    this._input1Value = plan ? plan.etd : -1;
                    this._input2Value = plan ? plan.departurePilotEtd : -1;
                    this.scheduledDeparture = scheduleEntry?.departurePortCall?.GSIS?.departureAt ?? null;
                    this.scheduledPilotDeparture = isVesselOnCloud ? (scheduleEntry?.departurePortCall?.GSIS?.estimatedPilotDropOff ?? null) : (scheduleEntry?.departurePortCall?.GSIS?.estimatedpilotdropoff ?? null);

                    if (scheduleEntry.departurePortCall?.current.actuals.departureAt) {
                        this._departureBerthSourceIsActual = true;
                        this.scheduledDeparture = scheduleEntry.departurePortCall?.current.actuals.departureAt;
                    }
                    if (scheduleEntry.departurePortCall?.current.actuals.pilotOffAt) {
                        this._pilotDepartureSourceIsActual = true;
                        this.scheduledPilotDeparture = scheduleEntry.departurePortCall?.current.actuals.pilotOffAt
                    }
                }
                else
                {
                    this._input1Value = plan ? plan.arrivalPilotEta : -1;
                    this._input2Value = plan ? plan.eta : -1;
                    this.scheduledArrival = scheduleEntry?.arrivalPortCall?.GSIS?.arrivalAt ?? null;
                    this.scheduledPilotArrival = isVesselOnCloud ? (scheduleEntry?.arrivalPortCall?.GSIS?.estimatedPilotBoarding ?? null) : (scheduleEntry?.arrivalPortCall?.GSIS?.estimatedpilotboarding ?? null);
                    
                    const arrivalActuals = scheduleEntry.arrivalPortCall?.current.actuals;
                    if (arrivalActuals) {
                        if (arrivalActuals.pilotOnAt || arrivalActuals.pilotStationArrivalAt) {
                            this.scheduledPilotArrival = isVesselOnCloud ?  arrivalActuals.pilotOnAt : arrivalActuals.pilotStationArrivalAt;
                        }
                        if (arrivalActuals.arrivalAt) {
                            this.scheduledArrival = arrivalActuals.arrivalAt;
                        }
                    }
                    if (scheduleEntry.arrivalPortCall?.current.actuals.pilotStationArrivalAt) {
                        this._pilotArrivalSourceIsActual = true;
                        this.scheduledPilotArrival = scheduleEntry.arrivalPortCall?.current.actuals.pilotStationArrivalAt

                    }
                    if (scheduleEntry.arrivalPortCall?.current.actuals.arrivalAt) {
                        this._arrivalBerthIsActual = true;
                        this.scheduledArrival = scheduleEntry.arrivalPortCall?.current.actuals.arrivalAt;
                    }
                }
            }
        }

        this._scheduleEntry = scheduleEntry;
    }

    private getLabelClass(editDepartureEtd: boolean, inputValue: number, scheduledDeparture : string | null, scheduledArrival: string | null) {
        if(editDepartureEtd){
            return getDeviationClass(scheduledDeparture, inputValue);
        } else {
            return getDeviationClass(scheduledArrival, inputValue);
        }
    }

    private async _applyChange(): Promise<void>
    {
        if (this.etd ? !this._departureEtdsValid() : !this._arrivalEtasValid()) return;

        this._working = true;
        let reCalcNeeded = false;
        try
        {
            const plan = await VesselPlanningModel!.getCurrentPlan(this.scheduleId);

            if (this.etd)
            {
                // Update schedule if needed
                if (this._input1Value !== new Date(this._scheduleEntry!.departureAt).getTime())
                {
                    const newPortCall = deepCopy(this._scheduleEntry!.departurePortCall!.current)
                    newPortCall.departureAt = new Date(this._input1Value).toISOString();
                    newPortCall.estimatedpilotdropoff = new Date(this._input2Value).toISOString();
                    await VoyageSchedulesModel!.changePortCall(newPortCall);
                }
                
                if (plan)
                {
                    if (this._input1Value !== plan.etd)
                    {
                        reCalcNeeded = true;
                        plan.etd = this._input1Value;
                    }
                    if (this._input2Value !== plan.departurePilotEtd)
                    {
                        plan.departurePilotEtd = this._input2Value;
                        // Update eta on relevant pilot point waypoints as well
                        for (const route of plan.routes)
                        {
                            const pilotPoints = route.route_geometry.filter( feature => feature.geometry.type === "Point" && feature.properties.pilotPoint );
                            if (pilotPoints.length >= 2)
                            {
                                reCalcNeeded = true;
                                pilotPoints[0].properties.eta = new Date(plan.departurePilotEtd).toISOString();
                            }
                        }
                    }    
                }
            }
            else
            {
                // Update schedule if needed
                if (this._input2Value !== new Date(this._scheduleEntry!.arrivalAt).getTime())
                {
                    const newPortCall = deepCopy(this._scheduleEntry!.arrivalPortCall!.current)
                    newPortCall.arrivalAt = new Date(this._input2Value).toISOString();
                    newPortCall.estimatedpilotboarding = new Date(this._input1Value).toISOString();
                    verifyETAOverlapWithDepartureTime(this._input2Value, this._scheduleEntry!, newPortCall);
                    await VoyageSchedulesModel!.changePortCall(newPortCall);
                }
                
                if (plan)
                {
                    if (this._input1Value !== plan.arrivalPilotEta)
                    {
                        const routes = plan.routes;

                        plan.arrivalPilotEta = this._input1Value;
                        // Update eta on relevant pilot point waypoints as well
                        for (const route of routes)
                        {
                            const pilotPoints = route.route_geometry.filter( feature => feature.geometry.type === "Point" && feature.properties.pilotPoint );
                            if (pilotPoints.length > 0)
                            {
                                reCalcNeeded = true;
                                pilotPoints[pilotPoints.length-1].properties.eta = new Date(plan.arrivalPilotEta).toISOString();
                            }
                        }
                    }
                    if (this._input2Value !== plan.eta)
                    {
                        reCalcNeeded = true;
                        plan.eta = this._input2Value;
                    }    
                }
            }
        
            if (plan && reCalcNeeded)
            {
                await recalcRoutes(plan);
            }

            this._working = false;
            this.dispatchEvent(new Event("close"));
        }
        catch (err)
        {
            this._working = false;
            console.warn("Updating schedule and/or plan failed: ", err)
            if (err instanceof Error)
            {
                VoyageDialogPopupManager.createDialog({
                    headerText: "Failed to update",
                    bodyText: "An error occurred when applying the change",
                    isError: true,
                    errorDetails: err.message
                });
            }
        }
    }

    private async _getPlan(scheduleEntry: ScheduleEntry){
        const currentPlan= await VesselPlanningModel!.getCurrentPlan(scheduleEntry.id);
        if (!currentPlan)
        {
            // Create plan from schedule if not already existing
           const plan = await this._createPlanFromSchedule(scheduleEntry!)
           addPrefillValuesToPassagePlan(plan.passagePlan);
           return plan;
        }

        return currentPlan;
    }

    private async _createPlanFromSchedule(gsis: ScheduleEntry)
    {
        const plan = await VesselPlanningModel!.createPlan(this.scheduleId);
        plan.departureTerminalCode = gsis.departureTerminalCode;
        plan.arrivalTerminalCode = gsis.arrivalTerminalCode;

        return plan;
    }

};
